/**
 * Help Article - Renders a single help article with markdown content
 */

import React, { useMemo, useEffect, useRef } from 'react';
import { Calendar, Tag, Link2 } from 'lucide-react';
import { HelpArticleData, HELP_CATEGORIES } from './types';
import { getArticleById, defaultHelpContent } from './helpContent';
import './helpArticleStyles.css';

export interface HelpArticleProps {
  article: HelpArticleData;
  onRelatedClick?: (articleId: string) => void;
  allArticles?: HelpArticleData[]; // All articles for link resolution
}

export function HelpArticle({ article, onRelatedClick, allArticles = [] }: HelpArticleProps) {
  const category = HELP_CATEGORIES.find(c => c.id === article.category);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  // Reset scroll position when article changes
  useEffect(() => {
    const scrollToTop = () => {
      // Scroll the article container
      if (containerRef.current) {
        containerRef.current.scrollTo({ top: 0, behavior: 'instant' });
      }
      
      // Also scroll parent scrollable containers
      const selectors = [
        '.fixed.inset-0.overflow-y-auto', // Main modal container
        '[class*="overflow-y-auto"]', // Any overflow container
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) {
          element.scrollTo({ top: 0, behavior: 'instant' });
        }
      }
      
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    
    // Scroll immediately and after a brief delay
    scrollToTop();
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 50);
  }, [article.id]); // Re-run when article ID changes

  // Comprehensive markdown-to-HTML conversion with article link support
  const htmlContent = useMemo(() => {
    return renderMarkdown(article.content, allArticles);
  }, [article.content, allArticles]);

  // Handle article link clicks using ref
  useEffect(() => {
    if (!contentRef.current || !onRelatedClick) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('.help-article-link') as HTMLElement;
      if (link) {
        const articleId = link.getAttribute('data-article-id');
        if (articleId) {
          e.preventDefault();
          e.stopPropagation();
          
          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('[HelpArticle] Link clicked:', articleId, {
              allArticlesCount: allArticles.length,
              found: allArticles.some(a => a.id === articleId)
            });
          }
          
          // Call the navigation handler
          onRelatedClick(articleId);
        }
      }
    };

    const contentDiv = contentRef.current;
    // Use capture phase to ensure we catch the event
    contentDiv.addEventListener('click', handleLinkClick, true);

    return () => {
      contentDiv.removeEventListener('click', handleLinkClick, true);
    };
  }, [htmlContent, onRelatedClick, allArticles]);

  // Get related articles (search in all articles, not just default)
  const relatedArticles = useMemo(() => {
    if (!article.relatedArticles) return [];
    return article.relatedArticles
      .map(id => getArticleById(id, allArticles.length > 0 ? allArticles : defaultHelpContent))
      .filter((a): a is HelpArticleData => a !== undefined);
  }, [article.relatedArticles, allArticles]);

  return (
    <div ref={containerRef} className="p-6">
      {/* Article Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          {category && (
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
              {category.name}
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          {article.title}
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">
          {article.summary}
        </p>
      </div>

      {/* Article Content */}
      <div 
        ref={contentRef}
        className="help-article-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        onClick={(e) => {
          // Additional React-level click handler as backup
          const target = e.target as HTMLElement;
          const link = target.closest('.help-article-link') as HTMLElement;
          if (link) {
            const articleId = link.getAttribute('data-article-id');
            if (articleId && onRelatedClick) {
              e.preventDefault();
              e.stopPropagation();
              onRelatedClick(articleId);
            }
          }
        }}
      />

      {/* Metadata */}
      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>Last updated: {article.lastUpdated}</span>
          </div>
          {article.keywords.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Tag className="h-4 w-4" />
              <span>{article.keywords.slice(0, 5).join(', ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Related Articles
          </h3>
          <div className="space-y-2">
            {relatedArticles.map(related => (
              <button
                key={related.id}
                onClick={() => onRelatedClick?.(related.id)}
                className="
                  w-full text-left p-3 rounded-lg
                  bg-slate-50 dark:bg-slate-700/30
                  hover:bg-slate-100 dark:hover:bg-slate-700
                  transition-colors
                "
              >
                <p className="font-medium text-slate-900 dark:text-white text-sm">
                  {related.title}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {related.summary}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Comprehensive markdown to HTML renderer
 * Handles full markdown syntax including nested lists, tables, code blocks, and article links
 */
function renderMarkdown(
  markdown: string, 
  allArticles: HelpArticleData[] = [],
  onArticleClick?: (articleId: string) => void
): string {
  let html = markdown;
  const lines = html.split('\n');
  const output: string[] = [];
  let inCodeBlock = false;
  let codeBlockLang = '';
  let codeBlockContent: string[] = [];
  let inTable = false;
  let tableRows: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' | null = null;
  let listItems: string[] = [];
  let listIndent = 0;

  // Helper to escape HTML (but preserve code blocks)
  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Helper to process inline markdown
  // Process links BEFORE escaping HTML to preserve link syntax
  const processInline = (text: string): string => {
    let result = text;
    const linkPlaceholders: Array<{ placeholder: string; replacement: string }> = [];
    let placeholderIndex = 0;
    
    // Process links FIRST (before escaping HTML) - use placeholders
    // Article links (#article-id format)
    result = result.replace(/\[([^\]]+)\]\(#([^)]+)\)/g, (match, linkText, articleId) => {
      const article = allArticles.find(a => a.id === articleId);
      const placeholder = `__LINK_PLACEHOLDER_${placeholderIndex++}__`;
      const safeId = articleId.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const safeText = linkText.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      let replacement: string;
      if (article) {
        replacement = `<a href="#" class="help-article-link" data-article-id="${safeId}">${safeText}</a>`;
      } else {
        replacement = `<a href="#${safeId}" class="help-article-link help-article-link-invalid" data-article-id="${safeId}">${safeText}</a>`;
      }
      linkPlaceholders.push({ placeholder, replacement });
      return placeholder;
    });
    
    // External links (must come after article links)
    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      // Skip if already processed as article link
      if (url.startsWith('#')) {
        return match;
      }
      const placeholder = `__LINK_PLACEHOLDER_${placeholderIndex++}__`;
      const safeUrl = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const safeText = linkText.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const replacement = `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
      linkPlaceholders.push({ placeholder, replacement });
      return placeholder;
    });
    
    // Now escape HTML
    result = escapeHtml(result);
    
    // Restore links
    linkPlaceholders.forEach(({ placeholder, replacement }) => {
      result = result.replace(placeholder, replacement);
    });
    
    // Bold (**text** or __text__)
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    result = result.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    
    // Italic (*text* or _text_) - but not if part of bold or inside links
    result = result.replace(/(?<!<[^>]*)\*([^*<]+)\*(?![^<]*>)/g, '<em>$1</em>');
    result = result.replace(/(?<!<[^>]*)_([^_<]+)_(?![^<]*>)/g, '<em>$1</em>');
    
    // Inline code - handle carefully to avoid breaking links
    result = result.replace(/(?<!<[^>]*)`([^`<]+)`(?![^<]*>)/g, '<code>$1</code>');
    
    return result;
  };

  // Process each line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const indent = line.length - line.trimStart().length;

    // Code blocks
    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        // Close code block
        const code = codeBlockContent.join('\n');
        output.push(`<pre><code class="language-${codeBlockLang}">${escapeHtml(code)}</code></pre>`);
        inCodeBlock = false;
        codeBlockContent = [];
        codeBlockLang = '';
      } else {
        // Open code block
        inCodeBlock = true;
        codeBlockLang = trimmed.slice(3).trim();
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    // Tables
    if (trimmed.includes('|') && trimmed.split('|').length > 2) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      
      // Check if it's a separator row
      if (/^\|[\s\-:]+\|/.test(trimmed)) {
        continue; // Skip separator rows
      }
      
      const cells = trimmed.split('|').slice(1, -1).map(c => c.trim());
      tableRows.push(cells);
      continue;
    } else {
      if (inTable && tableRows.length > 0) {
        // Close table
        let tableHtml = '<table class="help-table"><thead><tr>';
        const headerRow = tableRows[0];
        headerRow.forEach(cell => {
          tableHtml += `<th>${processInline(cell)}</th>`;
        });
        tableHtml += '</tr></thead><tbody>';
        
        for (let j = 1; j < tableRows.length; j++) {
          tableHtml += '<tr>';
          tableRows[j].forEach(cell => {
            tableHtml += `<td>${processInline(cell)}</td>`;
          });
          tableHtml += '</tr>';
        }
        
        tableHtml += '</tbody></table>';
        output.push(tableHtml);
        tableRows = [];
        inTable = false;
      }
    }

    // Headers
    if (trimmed.startsWith('###### ')) {
      closeListIfNeeded();
      output.push(`<h6>${processInline(trimmed.slice(7))}</h6>`);
      continue;
    }
    if (trimmed.startsWith('##### ')) {
      closeListIfNeeded();
      output.push(`<h5>${processInline(trimmed.slice(6))}</h5>`);
      continue;
    }
    if (trimmed.startsWith('#### ')) {
      closeListIfNeeded();
      output.push(`<h4>${processInline(trimmed.slice(5))}</h4>`);
      continue;
    }
    if (trimmed.startsWith('### ')) {
      closeListIfNeeded();
      output.push(`<h3>${processInline(trimmed.slice(4))}</h3>`);
      continue;
    }
    if (trimmed.startsWith('## ')) {
      closeListIfNeeded();
      output.push(`<h2>${processInline(trimmed.slice(3))}</h2>`);
      continue;
    }
    if (trimmed.startsWith('# ')) {
      closeListIfNeeded();
      output.push(`<h1>${processInline(trimmed.slice(2))}</h1>`);
      continue;
    }

    // Horizontal rules
    if (/^[-*_]{3,}$/.test(trimmed)) {
      closeListIfNeeded();
      output.push('<hr />');
      continue;
    }

    // Blockquotes
    if (trimmed.startsWith('> ')) {
      closeListIfNeeded();
      output.push(`<blockquote>${processInline(trimmed.slice(2))}</blockquote>`);
      continue;
    }

    // Lists
    const ulMatch = trimmed.match(/^([-*])\s+(.+)$/);
    const olMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    
    if (ulMatch || olMatch) {
      const currentIndent = Math.floor(indent / 2); // 2 spaces per indent level
      const isOrdered = !!olMatch;
      const itemText = ulMatch ? ulMatch[2] : olMatch![2];
      
      if (!inList || listType !== (isOrdered ? 'ol' : 'ul') || listIndent !== currentIndent) {
        closeListIfNeeded();
        inList = true;
        listType = isOrdered ? 'ol' : 'ul';
        listIndent = currentIndent;
        listItems = [];
      }
      
      listItems.push(`<li>${processInline(itemText)}</li>`);
      continue;
    }

    // Close list if we hit a non-list line
    if (inList && trimmed) {
      closeListIfNeeded();
    }

    // Regular paragraphs
    if (trimmed) {
      output.push(`<p>${processInline(trimmed)}</p>`);
    } else {
      // Empty line - close any open lists
      closeListIfNeeded();
      output.push(''); // Preserve spacing
    }
  }

  // Close any remaining open blocks
  closeListIfNeeded();
  if (inCodeBlock) {
    const code = codeBlockContent.join('\n');
    output.push(`<pre><code class="language-${codeBlockLang}">${escapeHtml(code)}</code></pre>`);
  }
  if (inTable && tableRows.length > 0) {
    let tableHtml = '<table class="help-table"><thead><tr>';
    const headerRow = tableRows[0];
    headerRow.forEach(cell => {
      tableHtml += `<th>${processInline(cell)}</th>`;
    });
    tableHtml += '</tr></thead><tbody>';
    for (let j = 1; j < tableRows.length; j++) {
      tableHtml += '<tr>';
      tableRows[j].forEach(cell => {
        tableHtml += `<td>${processInline(cell)}</td>`;
      });
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table>';
    output.push(tableHtml);
  }

  function closeListIfNeeded() {
    if (inList && listItems.length > 0) {
      const tag = listType === 'ol' ? 'ol' : 'ul';
      output.push(`<${tag} class="help-list">${listItems.join('')}</${tag}>`);
      listItems = [];
      inList = false;
      listType = null;
      listIndent = 0;
    }
  }

  return output.join('\n');
}





